import rawPostHog from "posthog-js";
import {
  analyticsAllowed,
  getPrivacyAccount,
  privacyEpoch,
  subscribePrivacyConsent,
} from "./privacy-consent";

// Every caller, including auth/onboarding stores, crosses this gate.
const posthog = new Proxy(rawPostHog, {
  get(target, property) {
    if (property === "__loaded") return analyticsAllowed() && target.__loaded;
    const value = Reflect.get(target, property);
    if (typeof value !== "function") return value;
    return (...args: unknown[]) =>
      analyticsAllowed() && property !== "startSessionRecording"
        ? Reflect.apply(value, target, args)
        : undefined;
  },
});

// SDK retries and queued requests bypass capture/before_send. Check consent at
// the actual transport boundary too, and retire requests from an earlier grant.
let resettingIdentity = false;
const sendRetriableRequest =
  rawPostHog._send_retriable_request?.bind(rawPostHog);
if (sendRetriableRequest) {
  rawPostHog._send_retriable_request = (options) => {
    const request = options as typeof options & { denkerConsentEpoch?: number };
    request.denkerConsentEpoch ??= privacyEpoch();
    if (request.denkerConsentEpoch === privacyEpoch())
      sendRetriableRequest(request);
  };
}
const sendRequest = rawPostHog._send_request?.bind(rawPostHog);
if (sendRequest) {
  rawPostHog._send_request = (options) => {
    const request = options as typeof options & { denkerConsentEpoch?: number };
    request.denkerConsentEpoch ??= privacyEpoch();
    if (
      resettingIdentity ||
      !analyticsAllowed() ||
      request.denkerConsentEpoch !== privacyEpoch()
    )
      return;
    if (
      request.batchKey === "recordings" ||
      /\/s\/(?:[?#]|$)/.test(request.url)
    )
      return;
    sendRequest(request);
  };
}
import { getTelemetryContext } from "./telemetry-context";
import { releaseChannel } from "./release-channel";

import type { DesktopLaunchOutcome } from "@/lib/desktop-deep-link-launcher";

/** One desktop release carries both channels' project keys and reports to
 *  the one it runs on; local builds and the web app keep their single key. */
function posthogKey(): string | undefined {
  const channel = releaseChannel();
  const key =
    channel === "production"
      ? import.meta.env.VITE_POSTHOG_KEY_PROD
      : channel === "preview"
        ? import.meta.env.VITE_POSTHOG_KEY_PREVIEW
        : import.meta.env.VITE_POSTHOG_KEY;
  return (key as string | undefined) || undefined;
}
const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string) || "https://eu.i.posthog.com";
const POSTHOG_ENABLE_LOCAL =
  (import.meta.env.VITE_POSTHOG_ENABLE_LOCAL as string | undefined) === "1";

const PH_PARAM = "ph";

export type Surface = "web" | "desktop";

let registeredSurface: Surface = "web";
let sdkReady = rawPostHog.__loaded;
let consentSubscribed = false;
const readyCallbacks = new Set<() => void>();
let lastAccount = getPrivacyAccount();

function discardSessionReplay(): void {
  const recorder = rawPostHog.sessionRecording as
    | { _discardRecording?: () => void }
    | undefined;
  recorder?._discardRecording?.();
  rawPostHog.stopSessionRecording();
}

export function canLoadPostHog(): boolean {
  return Boolean(
    analyticsAllowed() &&
    posthogKey() &&
    (POSTHOG_ENABLE_LOCAL ||
      (releaseChannel() !== "local" && import.meta.env.MODE !== "development")),
  );
}

/**
 * Read `?ph=<distinct_id>` — the anonymous landing distinct_id, handed off by
 * the landing site's PostHogProvider — and strip it so bookmarks and shared
 * links stay clean.
 *
 * The caller passes it to `posthog.init` as `bootstrap.distinctID` with
 * `isIdentifiedID: false`. It must NOT be passed to `posthog.identify()`:
 * that would mark the landing person identified, and PostHog would then
 * refuse to merge it into the user's person at login ("Refused to merge an
 * already identified user"), orphaning the acquisition history this handoff
 * exists to preserve.
 */
function takeLandingDistinctId(): string | null {
  try {
    const url = new URL(window.location.href);
    const landingId = url.searchParams.get(PH_PARAM);
    if (!landingId) return null;
    url.searchParams.delete(PH_PARAM);
    window.history.replaceState({}, "", url.toString());
    return landingId;
  } catch {
    // Malformed URL or a browser without the history API.
    return null;
  }
}

/** Explicit product events only. DOM recording and autocapture stay disabled. */
export function initPostHog() {
  if (!consentSubscribed) {
    consentSubscribed = true;
    subscribePrivacyConsent(() => {
      const account = getPrivacyAccount();
      if (account !== lastAccount && rawPostHog.__loaded) {
        resettingIdentity = true;
        try {
          rawPostHog.reset(true);
        } finally {
          resettingIdentity = false;
        }
      }
      lastAccount = account;
      if (!analyticsAllowed()) {
        readyCallbacks.clear();
        if (rawPostHog.__loaded) {
          discardSessionReplay();
          rawPostHog.opt_out_capturing();
        }
        return;
      }
      if (rawPostHog.__loaded) {
        rawPostHog.opt_in_capturing({ captureEventName: false });
        discardSessionReplay();
      } else initPostHog();
    });
  }
  if (!canLoadPostHog()) return;
  if (rawPostHog.__loaded) return;
  const key = posthogKey();
  if (!key) return;

  const landingId = takeLandingDistinctId();

  rawPostHog.init(key, {
    api_host: POSTHOG_HOST,
    ui_host: "https://eu.posthog.com",
    person_profiles: "identified_only",
    ...(landingId
      ? { bootstrap: { distinctID: landingId, isIdentifiedID: false } }
      : {}),
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    autocapture: false,
    request_batching: false,
    opt_out_persistence_by_default: true,
    advanced_disable_flags: true,
    disable_surveys: true,
    disable_product_tours: true,
    disable_conversations: true,
    before_send: (event) =>
      analyticsAllowed() && event?.event !== "$snapshot" ? event : null,
    // Share cookie across *.denker.ai so landing → app journey is tracked as one user
    cookie_domain: ".denker.ai",
    // Inject app label into every event so one PostHog project can serve
    // multiple apps. `surface` defaults to "web" and is overridden by the
    // desktop entry via `registerSurface("desktop")` so dashboards can split.
    loaded: (ph) => {
      sdkReady = true;
      if (!analyticsAllowed()) {
        ph.opt_out_capturing();
        return;
      }
      ph.register({
        app: "dolcetto",
        surface: registeredSurface,
        ...getTelemetryContext(),
      });
      discardSessionReplay();
      for (const callback of Array.from(readyCallbacks)) {
        readyCallbacks.delete(callback);
        callback();
      }
    },
  } as Parameters<typeof posthog.init>[1]);
}

/**
 * Run a callback once PostHog's browser SDK is ready. If it is already ready,
 * the callback runs immediately. Returns an unsubscribe function.
 */
export function onPostHogReady(callback: () => void): () => void {
  if (!canLoadPostHog()) return () => {};
  if (sdkReady || posthog.__loaded) {
    sdkReady = true;
    callback();
    return () => {};
  }
  readyCallbacks.add(callback);
  return () => {
    readyCallbacks.delete(callback);
  };
}

/**
 * Override the default `surface: "web"` super-property. Call from non-web
 * entrypoints (desktop today, future mobile) AFTER `initPostHog` and BEFORE
 * the first event capture. All subsequent events carry the new surface.
 */
export function registerSurface(surface: Surface) {
  registeredSurface = surface;
  if (!sdkReady && !posthog.__loaded) return;
  posthog.register({ surface });
}

/**
 * Same event name the landing download page emits, so one PostHog insight
 * covers every download entry point. Landing events carry `app: "landing"`
 * and `source: "download_page"`; these carry `app: "dolcetto"` plus the
 * in-app source below.
 */
/**
 * A desktop download started, counted per platform.
 *
 * `mac_download_started` predates Windows and is what existing dashboards
 * count. Renaming it would silently reset them; reusing it for Windows would
 * quietly corrupt them. So the event name carries the platform and the old one
 * keeps meaning exactly what it always did.
 */
export function trackDesktopDownloadStarted(
  source: "desktop_handoff" | "login_page",
  platform: "Mac" | "Windows",
) {
  onPostHogReady(() => {
    posthog.capture(
      platform === "Windows"
        ? "windows_download_started"
        : "mac_download_started",
      { source },
    );
  });
}

/** Fired on mount of /auth/desktop-handoff — the page every browser user
 *  is gated to. Without it, a signup that bounces here is invisible. */
export function trackDesktopHandoffViewed(onMac: boolean) {
  onPostHogReady(() => {
    posthog.capture("desktop_handoff_viewed", { on_mac: onMac });
  });
}

export function trackDesktopOpenClicked() {
  onPostHogReady(() => {
    posthog.capture("desktop_open_clicked");
  });
}

/** Outcome events use sendBeacon: "failed-reloading" is followed by an
 *  immediate location.reload() and "launched" by the tab hiding, so a
 *  queued XHR would be dropped. */
export function trackDesktopOpenOutcome(outcome: DesktopLaunchOutcome) {
  onPostHogReady(() => {
    if (outcome === "launched") {
      posthog.capture("desktop_open_succeeded", undefined, {
        transport: "sendBeacon",
      });
      return;
    }
    posthog.capture(
      "desktop_open_failed",
      { will_reload: outcome === "failed-reloading" },
      { transport: "sendBeacon" },
    );
  });
}

export { posthog };
